/**
 * Comprehensive storage cleanup utility for logout
 * Clears all localStorage, sessionStorage, cookies, and cache
 */

/**
 * Clear all cookies for the current domain
 */
const clearAllCookies = (): void => {
  try {
    // Get all cookies
    const cookies = document.cookie.split(';');
    
    // Clear each cookie by setting it to expire in the past
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Clear cookie for current path
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      
      // Clear cookie for root path
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      
      // Clear cookie without domain (for localhost)
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=`;
    });
  } catch (error) {
    console.error('Error clearing cookies:', error);
  }
};

/**
 * Clear browser cache (if possible)
 */
const clearBrowserCache = (): void => {
  try {
    // Clear cache using Cache API if available
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
  } catch (error) {
    console.error('Error clearing browser cache:', error);
  }
};

/**
 * Clear all application-specific localStorage items
 */
const clearApplicationStorage = (): void => {
  try {
    // List of all known localStorage keys used by the application
    const storageKeys = [
      // Token-related (handled by tokenManager but included for completeness)
      'access_token',
      'id_token',
      'refresh_token',
      'token_expires_at',
      'user_info',
      
      // Inventory cache
      'tagops_inventory_cache',
      
      // Config cache
      'tagops_service_config_cache',
      'tagops_accounts_cache',
      
      // Scan cache
      'tagops_single_scan_cache',
      'tagops_bulk_scan_cache',
      'tagops_full_account_scan_cache',
      'tagops_scan_settings_cache',
      
      // Activity tracking
      'tagops_last_activity',
      
      // Account notifications
      'accountNotifications',
      
      // Note: We're keeping 'tagops-theme-mode' as theme preference
      // is typically user preference, not user-specific data
      // If you want to clear it too, uncomment the line below:
      // 'tagops-theme-mode',
    ];
    
    // Remove each key
    storageKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove localStorage key: ${key}`, error);
      }
    });
    
    // Also clear any localStorage items that start with 'tagops_' as a safety measure
    try {
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.startsWith('tagops_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Error clearing tagops_ prefixed keys:', error);
    }
  } catch (error) {
    console.error('Error clearing application storage:', error);
  }
};

/**
 * Clear all sessionStorage items
 */
const clearSessionStorage = (): void => {
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Error clearing sessionStorage:', error);
  }
};

/**
 * Comprehensive cleanup function that clears all storage, cookies, and cache
 * Call this function when logging out to ensure no user data persists
 */
export const clearAllStorage = (): void => {
  console.log('Clearing all storage, cookies, and cache...');
  
  // Clear application-specific localStorage
  clearApplicationStorage();
  
  // Clear sessionStorage
  clearSessionStorage();
  
  // Clear all cookies
  clearAllCookies();
  
  // Clear browser cache
  clearBrowserCache();
  
  console.log('Storage cleanup completed');
};

export default clearAllStorage;
